import React, {useCallback, useEffect, useState} from 'react';
import {
    getSlaAccumulationRate,
    getSlaAvailabilityRate,
    getSlaEvaluationMonthRate,
    getSlaImproveAndLeak,
    getSlaIncidentRepeatCount,
    getSlaIncidentReportCount,
    getSlaMonthIncidentRecover,
    getSlaMonthProcessRate,
    getSlaTotalRate,
} from "../../api/static/StaticService";
import {useSelector} from "react-redux";
import {getDate} from "../../redux/slice/dateSlice";
import DashBoardSalDateSearch from "./DashBoard_SAL_Date_Search";
import {
    SlaDualGaugeCard,
    SlaLineLegendCard,
    SlaMonthlyBarCard,
    SlaSingleLineCard,
    SlaTotalRateCard,
} from "../chart/SlaDashboardWidgets";

const extractData = (response) => (response?.status === 200 ? response.data : null);

const normalizeIncidentRecover = (response) => {
    const payload = extractData(response);
    if (!payload) return null;

    const series = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];
    if (!series.length) return {title: payload.title ?? "월별 장애 복구율", data: []};

    const first = series[0];
    return {
        title: first.id ?? payload.title ?? "월별 장애 복구율",
        data: [{
            id: first.id,
            color: first.color,
            data: first.data,
        }],
    };
};

function DashBoard_SLA() {
    const initialMonth = useSelector((state) => getDate(state, "SLA 대시보드"));
    const [month, setMonth] = useState(initialMonth);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        totalRate: null,
        accumulation: null,
        evaluationMonth: null,
        availability: null,
        incidentRepeat: null,
        incidentReport: null,
        incidentRecover: null,
        processRate: null,
        improveLeak: null,
    });

    const fetchDashboard = useCallback(async () => {
        setLoading(true);

        const [
            totalRate,
            accumulation,
            evaluationMonth,
            availability,
            incidentRepeat,
            incidentReport,
            incidentRecover,
            processRate,
            improveLeak,
        ] = await Promise.all([
            getSlaTotalRate(month),
            getSlaAccumulationRate(month),
            getSlaEvaluationMonthRate(month),
            getSlaAvailabilityRate(month),
            getSlaIncidentRepeatCount(month),
            getSlaIncidentReportCount(month),
            getSlaMonthIncidentRecover(month),
            getSlaMonthProcessRate(month),
            getSlaImproveAndLeak(month),
        ]);

        const accumulationBody = extractData(accumulation);

        setDashboardData({
            totalRate: extractData(totalRate),
            accumulation: accumulationBody?.data ?? accumulationBody,
            evaluationMonth: extractData(evaluationMonth),
            availability: extractData(availability),
            incidentRepeat: extractData(incidentRepeat),
            incidentReport: extractData(incidentReport),
            incidentRecover: normalizeIncidentRecover(incidentRecover),
            processRate: extractData(processRate),
            improveLeak: extractData(improveLeak),
        });
        setLoading(false);
    }, [month]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    return (
        <div className="common-container flex min-h-0 flex-col">
            <div className="mb-3 shrink-0">
                <DashBoardSalDateSearch month={month} setMonth={setMonth} />
            </div>

            <div
                className="flex flex-col gap-4 md:grid md:min-h-0 md:flex-1 md:[grid-template-columns:repeat(3,minmax(0,1fr))] md:[grid-template-rows:repeat(3,minmax(0,1fr))]"
            >
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaTotalRateCard data={dashboardData.totalRate} loading={loading} />
                </div>
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaDualGaugeCard data={dashboardData.accumulation} loading={loading} />
                </div>
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaMonthlyBarCard data={dashboardData.evaluationMonth} loading={loading} />
                </div>

                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaLineLegendCard
                        data={dashboardData.availability}
                        loading={loading}
                        title="일별 가동률 통계"
                    />
                </div>
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaMonthlyBarCard
                        data={dashboardData.incidentRepeat}
                        loading={loading}
                        title="월별 장애 건 수 / 동일장애 발생 건 수"
                    />
                </div>
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaLineLegendCard
                        data={dashboardData.incidentReport}
                        loading={loading}
                        title="월별 적기보고율 / 일정준수율"
                    />
                </div>

                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaSingleLineCard
                        data={dashboardData.incidentRecover}
                        loading={loading}
                        title="월별 장애 복구율"
                    />
                </div>
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaLineLegendCard
                        data={dashboardData.processRate}
                        loading={loading}
                        title="월별 적기처리율"
                    />
                </div>
                <div className="flex min-h-[300px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <SlaLineLegendCard
                        data={dashboardData.improveLeak}
                        loading={loading}
                        title="월별 성능개선제안 / 유출손실"
                    />
                </div>
            </div>
        </div>
    );
}

export default DashBoard_SLA;
